import React, { useEffect, useState, useRef } from 'react';
import { fetchBriefingData, createShareLink, type BriefingData } from '../lib/api';
import { designTokens } from '../styles/design-tokens';
import { REPORT } from './print/PrintAtoms';
import { ScrollSyncProvider, useScrollSync } from './briefing/ScrollSyncProvider';
import { BriefingMap, type BriefingMapHandle } from './briefing/BriefingMap';
import { BriefingContent } from './briefing/BriefingContent';

interface SiteBriefingProps {
  parcelId: string;
  token?: string;
}

const ScrollPanel: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ children, className, style }) => {
  const { handleScroll } = useScrollSync();
  return (
    <div
      className={className}
      style={style}
      onScroll={(e) => handleScroll(e.currentTarget)}
    >
      {children}
    </div>
  );
};

export const SiteBriefing: React.FC<SiteBriefingProps> = ({ parcelId, token }) => {
  const [data, setData] = useState<BriefingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareModal, setShareModal] = useState<{ url: string; token: string; redaction?: 'partner' | null } | null>(null);
  const [shareLabelPrompt, setShareLabelPrompt] = useState(false);
  const [shareLabel, setShareLabel] = useState('');
  const [sharePartnerRedaction, setSharePartnerRedaction] = useState(false);
  const [shareCreating, setShareCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [mapSnapshots, setMapSnapshots] = useState<{ cover: string; fuelDelivery: string; infrastructure: string } | null>(null);
  const mapRef = useRef<BriefingMapHandle>(null);

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  // Opt-in carve-out disambiguation via ?carveOut=<slug>; absent → byte-identical.
  const carveOut = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('carveOut') || undefined)
    : undefined;
  // Seller-alternatives edition (?layouts=alt) — Crown Pine A1 only; the server
  // ignores it everywhere else. Carried onto the share URL and the memo link so
  // the "A1 Alt" link round-trips without dropping back to the original.
  const layoutsAlt = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('layouts') === 'alt';
  // Which site outline the map is showing ('original' or an alternative key).
  // Lifted here so the hero acreage and the Site Intelligence banner follow the
  // map's SITE LAYOUT switcher. Only the A1 Alt edition ever changes it.
  const [selectedSiteLayout, setSiteLayout] = useState<string | null>(null);
  // Default from the authorized response: shared tokens can carry the alternative
  // edition even without URL flags. Keep an explicit choice through lens refetches.
  const siteLayout = selectedSiteLayout ?? (
    data?.carveOutAggregate?.slug === 'crownpine-blk-194-a1'
      && data.siteLayouts?.rows.some(row => row.key === 'alt3')
      ? 'alt3' : 'original'
  );

  // Partner-redacted editions are proven ONLY for the Crown Pine A1 carve-out — see
  // docs/research/crownpine-siting-dossier/a1-briefing-canon.md §4. The server enforces
  // this independently; this gate only decides whether the checkbox renders at all.
  const isA1Redactable = parcelId === '912d47ae-b055-4716-9375-7946623b7f3b' && carveOut === 'crownpine-blk-194-a1';

  // Grid lens is URL state (?profile=grid) so deep links and share links pin
  // the view the recipient sees. Token views honor the param but never render
  // the toggle (BriefingContent gets no onLensChange), so the protected client
  // share links cannot change.
  const [profile, setProfile] = useState<'btm' | 'grid'>(() =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('profile') === 'grid' ? 'grid' : 'btm');

  useEffect(() => {
    setLoading(true);
    // Clear any prior error before refetching. `if (error)` returns above the
    // rendered briefing, so a failed lens refetch would otherwise replace a
    // working page with "Site not found." and take the toggle down with it,
    // leaving no way back except hand-editing ?profile=grid out of the URL.
    setError(null);
    fetchBriefingData(parcelId, token, profile === 'grid' ? 'grid' : undefined, carveOut, layoutsAlt ? 'alt' : undefined)
      .then(setData)
      .catch((e: any) => setError(e?.response?.status === 410 ? 'This share link has expired.' : 'Site not found.'))
      .finally(() => setLoading(false));
  }, [parcelId, token, profile, carveOut, layoutsAlt]);

  const handleLensChange = (next: 'btm' | 'grid') => {
    setProfile(next);
    const url = new URL(window.location.href);
    if (next === 'grid') url.searchParams.set('profile', 'grid');
    else url.searchParams.delete('profile');
    window.history.replaceState(null, '', url.toString());
  };

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Full-page loader only when nothing is rendered yet. A lens refetch keeps
  // the current tree mounted (data swaps in place) — unmounting here mid-switch
  // tears down BriefingMap while mapbox is disposing and crashes its cleanup.
  if (loading && !data) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: designTokens.typography.fontFamily.display, color: REPORT.secondary }}>
      Loading briefing...
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: designTokens.typography.fontFamily.display, color: REPORT.fail }}>
      {error}
    </div>
  );

  const handlePrint = async () => {
    // Capture map snapshots before printing
    if (mapRef.current) {
      try {
        const snapshots = await mapRef.current.captureSnapshots();
        setMapSnapshots(snapshots);
        // Wait for React to render the snapshot images
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch {
        // Print without snapshots if capture fails
      }
    }
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      setTimeout(() => window.print(), 500);
    } else {
      window.print();
    }
  };

  const handleShare = () => {
    setShareLabel('');
    setSharePartnerRedaction(false);
    setShareLabelPrompt(true);
  };

  const handleShareCreate = async () => {
    try {
      setShareCreating(true);
      setShareError(null);
      // Server persists the carve-out lens on the token row and returns the URL
      // with ?carveOut= already applied, so the admin Shares archive shows the
      // exact link the client received.
      // The seller-alternatives edition is stored on the token row (migration 183)
      // and the server returns the URL with &layouts=alt applied, so the admin
      // Shares "copy URL" rebuilds the exact link. Never on a partner mint.
      const result = await createShareLink(
        parcelId, 30, shareLabel || undefined, carveOut || undefined, profile === 'grid' ? 'grid' : undefined,
        isA1Redactable && sharePartnerRedaction ? 'partner' : undefined,
        layoutsAlt && !(isA1Redactable && sharePartnerRedaction) ? 'alt' : undefined,
      );
      setShareLabelPrompt(false);
      setShareModal(result);
      setCopied(false);
    } catch {
      setShareError('Failed to create share link.');
      setTimeout(() => setShareError(null), 4000);
    } finally {
      setShareCreating(false);
    }
  };

  const handleCopy = () => {
    if (shareModal) {
      navigator.clipboard.writeText(shareModal.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!data) return null;

  // Show the lens toggle only where the grid rubric has real curated node intel
  // behind it — outside a curated radius 13.5 of its 27 curated points are
  // neutral placeholders. Token views never get it at all, which is what keeps
  // the protected client links from sprouting a control.
  // The `profile === 'grid'` clause keeps the ?profile=grid escape hatch
  // escapable: without it, forcing grid on an uncurated parcel would strand the
  // viewer with no control to return.
  const showLensToggle = !token && (!!data?.gridLensAvailable || profile === 'grid');

  return (
    <ScrollSyncProvider>
      <style>{`
        @media print {
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 0.55in 0.5in 0.5in; size: letter portrait; }
          body { orphans: 3; widows: 3; background: #ffffff !important; }
          .briefing-map-panel { display: none !important; }
          .briefing-map-snapshot { display: block !important; }
          .briefing-content-panel { width: 100% !important; overflow: visible !important; background: #ffffff !important; }
          .briefing-controls { display: none !important; }
          .methodology-content { display: block !important; }
          .no-break { break-inside: avoid; page-break-inside: avoid; }
          .briefing-page-break { break-before: page; page-break-before: always; }
          .briefing-keep-next { break-after: avoid; page-break-after: avoid; }
          .briefing-section-head { break-inside: avoid; page-break-inside: avoid; break-after: avoid; page-break-after: avoid; }
          h1, h2, h3, h4 { break-after: avoid; page-break-after: avoid; }
          .briefing-cover { display: flex !important; break-after: page; page-break-after: always; }
          a[href]::after { content: none !important; }
          tr { break-inside: avoid; page-break-inside: avoid; }
          img { break-inside: avoid; page-break-inside: avoid; }

          /* Jobs pass: strip the gray-card wallpaper. Section bodies sit on white,
             separated by rules + whitespace. Gray fill reserved for the grade cards,
             Confirm Before LOI, Key Numbers, and the dark header/footer bands. */
          .briefing-section {
            background: #ffffff !important;
            padding: 10px 24px 8px !important;
            border-top: 1px solid rgba(46,56,64,0.08) !important;
            margin-top: 0 !important;
          }

          /* Kill redundant Readiness summary bars — findings above already tell the story */
          .briefing-readiness-summary { display: none !important; }

          /* Keep section heading + intro + first finding row together on one page */
          .briefing-section-head + * {
            break-before: avoid !important;
            page-break-before: avoid !important;
          }

          /* Shrink section intros — stacked back-to-back they read like filler in print.
             Targets the intro <div> that follows SectionTitle inside briefing-section-head. */
          .briefing-section-head > div + div {
            font-size: 9.5px !important;
            line-height: 1.45 !important;
            margin-bottom: 6px !important;
            color: rgba(62, 78, 92, 0.72) !important;
          }

          /* Kill the colored left-border + tinted background on finding rows — badge alone carries severity */
          .briefing-finding-row {
            border-left: 0 !important;
            padding-left: 0 !important;
            background: transparent !important;
            border-radius: 0 !important;
            margin-bottom: 3px !important;
            border-bottom: 1px dotted rgba(46,56,64,0.10) !important;
            padding-bottom: 4px !important;
          }

          /* Footer stays dark and tight */
          .briefing-footer-print { padding: 8px 24px !important; margin-top: 12px !important; }

        }
        @media (max-width: 767px) {
          .briefing-view-toggle { display: none !important; }
        }
        .briefing-memo-pill:hover { background: #C4705A !important; color: #ffffff !important; }
        .briefing-memo-pill:hover .briefing-memo-arrow { transform: translateX(2px); }
        @media print {
          .briefing-memo-pill { display: none !important; }
        }
      `}</style>
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        height: '100vh', fontFamily: designTokens.typography.fontFamily.display,
      }}>
        <BriefingMap
          ref={mapRef}
          className="briefing-map-panel"
          parcel={data.parcel}
          pipelines={data.pipelines}
          mapLayers={data.mapLayers}
          nearestFeatures={data.nearestFeatures}
          carveOuts={data.carveOuts}
          meinersFootprint={data.meinersFootprint}
          siteLayout={siteLayout}
          onSiteLayoutChange={setSiteLayout}
          onPrint={handlePrint}
          onShare={token ? undefined : handleShare}
          parcelId={parcelId}
          token={token}
          style={isMobile ? { height: 250, position: 'sticky' as const, top: 0, zIndex: 10 } : undefined}
        />
        <ScrollPanel className="briefing-content-panel" style={{ width: isMobile ? '100%' : '55%', background: REPORT.white, overflow: 'auto', flex: isMobile ? 1 : undefined }}>
          <BriefingContent
            data={data}
            mapSnapshots={mapSnapshots}
            parcelId={parcelId}
            token={token}
            siteLayout={siteLayout}
            lensProfile={profile}
            onLensChange={showLensToggle ? handleLensChange : undefined}
          />
        </ScrollPanel>
      </div>

      {shareError && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: REPORT.fail, color: REPORT.failWash, padding: '8px 16px', borderRadius: 6,
          fontSize: 12, fontWeight: 600, zIndex: 110, whiteSpace: 'nowrap',
        }}>
          {shareError}
        </div>
      )}

      {shareLabelPrompt && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100,
        }}>
          <div style={{
            background: REPORT.white, borderRadius: 10, padding: '24px', maxWidth: 400, width: '90%',
            fontFamily: designTokens.typography.fontFamily.display,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: REPORT.heading, marginBottom: 4 }}>Name this share link</div>
            <div style={{ fontSize: 11, color: REPORT.body, marginBottom: 12 }}>Optional — helps you track who viewed it in the admin panel.</div>
            <input
              value={shareLabel}
              onChange={e => setShareLabel(e.target.value)}
              placeholder='e.g. "Taylor County site brief"'
              maxLength={100}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleShareCreate()}
              style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${REPORT.rule}`, borderRadius: 4, boxSizing: 'border-box' }}
            />
            {isA1Redactable && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sharePartnerRedaction}
                  onChange={e => setSharePartnerRedaction(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  <div style={{ fontSize: 12, fontWeight: 600, color: REPORT.heading }}>Partner-redacted edition</div>
                  <div style={{ fontSize: 11, color: REPORT.body }}>Strips pricing &amp; client identity — for counterparty viewing</div>
                </span>
              </label>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                onClick={handleShareCreate}
                disabled={shareCreating}
                style={{ padding: '8px 16px', background: REPORT.heading, color: REPORT.patina, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', opacity: shareCreating ? 0.6 : 1 }}
              >
                {shareCreating ? 'Creating...' : 'Create Link'}
              </button>
              <button onClick={() => setShareLabelPrompt(false)} style={{ padding: '8px 16px', background: REPORT.surface, color: REPORT.body, border: `1px solid ${REPORT.rule}`, borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {shareModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100,
        }}>
          <div style={{
            background: REPORT.white, borderRadius: 10, padding: '24px', maxWidth: 480, width: '90%',
            fontFamily: designTokens.typography.fontFamily.display,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: REPORT.heading }}>Share Briefing</div>
              {shareModal.redaction === 'partner' && (
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: REPORT.patina, background: REPORT.heading, padding: '2px 6px', borderRadius: 3 }}>
                  PARTNER-REDACTED
                </span>
              )}
            </div>
            <input
              value={shareModal.url}
              readOnly
              onClick={e => (e.target as HTMLInputElement).select()}
              style={{ width: '100%', padding: '8px 12px', fontSize: 12, border: `1px solid ${REPORT.rule}`, borderRadius: 4, boxSizing: 'border-box', fontFamily: 'monospace' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={handleCopy} style={{ padding: '8px 16px', background: REPORT.heading, color: REPORT.patina, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
              <button onClick={() => setShareModal(null)} style={{ padding: '8px 16px', background: REPORT.surface, color: REPORT.body, border: `1px solid ${REPORT.rule}`, borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                Close
              </button>
            </div>
            <div style={{ fontSize: 10, color: REPORT.tertiary, marginTop: 8 }}>
              Expires in 30 days. Anyone with this link can view the briefing.
            </div>
          </div>
        </div>
      )}
    </ScrollSyncProvider>
  );
};
